import { Component, type ErrorInfo, type ReactNode } from 'react';

/*
 * WHAT AN UNCAUGHT RENDER ERROR DID BEFORE THIS EXISTED, AND WHY IT WAS
 * REPORTED AS THE APP FREEZING.
 *
 * 🔴 **There was NO error boundary anywhere in this app** (measured 2026-09-03:
 * no `componentDidCatch`, no `getDerivedStateFromError`, nothing). Since React
 * 16 an error thrown during render that nothing catches does not fail that one
 * component — **React unmounts the ENTIRE TREE from the root**. The sidebar,
 * the top bar and the page all disappear, `#root` is left empty, and there is
 * no way back from inside the app because there is no app left to click. A
 * reload is the only recovery.
 *
 * ⚠️ **That is exactly the shape of the report this was written for**: *"after
 * voiding, deleting or editing, the UI freezes and needs a refresh,
 * sometimes."* Those three actions are the ones that change what a list holds
 * and then re-render it — a row whose relation has just been removed, a
 * response one field thinner than the last, a status id that no longer resolves
 * — which is precisely when a renderer reads a property of `undefined`. The
 * throw is in a render, so the whole screen goes, and to a person watching it
 * is indistinguishable from the app having hung.
 *
 * ⚠️ **This is the SECOND cause found for that report and it does not retire
 * the first.** The other was `useBodyScrollLock` leaving `document.body` at
 * `overflow: hidden` when two overlays overlapped, which freezes SCROLLING with
 * everything still on screen. They are different failures that a person
 * describes with the same sentence, and either one alone would produce the
 * report. Do not read a fix for one as a fix for the other.
 *
 * ── WHAT THIS DOES NOT DO ──────────────────────────────────────────────────
 *
 * ⚠️ **It does not stop anything throwing, and it must never be mistaken for
 * having fixed the underlying bug.** It converts an unrecoverable blank screen
 * into a visible, named failure that a person can leave — and it keeps the
 * error in the console, where it is still diagnosable. A boundary that
 * swallowed the error silently would trade a loud bug for a quiet one.
 *
 * ⚠️ **It catches RENDER errors only.** An error thrown in an event handler, a
 * promise, a `setTimeout` or an async `catch` does not reach a boundary and
 * never did — those paths still need their own handling, and every one of this
 * app's fetches already has it through `ApiError`.
 */

interface ErrorBoundaryProps {
  /**
   * What is inside, in the words of the person who will read the console line —
   * "the page", "the app". It is NOT rendered to the user: a reader does not
   * care which React subtree failed, and naming one in the UI reads as an
   * internal detail leaking.
   */
  scope: string;
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  // A class, and it has to be: `getDerivedStateFromError` and
  // `componentDidCatch` have no hook equivalent, which is the one remaining
  // thing hooks cannot express. This is the only class component in `src/`.
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    /*
     * ⚠️ **Still logged, and the COMPONENT STACK with it.** Catching an error
     * is not the same as understanding it: without this line the boundary would
     * turn a stack trace naming the exact component into a tidy message naming
     * nothing, and the defect would get harder to find rather than easier.
     * There is no error-reporting service wired up in this app, so the console
     * is the whole trail.
     */
    console.error(`[EMERGE] Uncaught error in ${this.props.scope}:`, error, info.componentStack);
  }

  private retry = (): void => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    const { error } = this.state;
    if (error === null) return this.props.children;

    return (
      /*
       * ⚠️ **PLAIN MARKUP AND DESIGN TOKENS, NOT `Panel` AND `Button`, AND THAT
       * IS DELIBERATE RATHER THAN AN OVERSIGHT OF THE BUILD-FROM-PRIMITIVES
       * RULE.** This renders only when something has already thrown during a
       * render. If the fallback itself mounts a component that throws — the
       * primitive that failed, or anything reaching into a context the failure
       * came from — the boundary throws while handling an error, which React
       * propagates to the boundary ABOVE it and, at the root, unmounts the tree
       * anyway. The one screen that must never fail is this one, so it uses
       * nothing that can.
       *
       * The same knowing-exception standing as `MinTableWidth` and the note
       * button on `UniversityPartnershipsPage`, and recorded the same way.
       */
      <div role="alert" className="mx-auto grid max-w-lg gap-3 px-4 py-10 text-sm">
        <h2 className="m-0 text-base font-semibold text-fg">Something went wrong on this screen</h2>
        {/*
         * ⚠️ **NOT helper text — this is an error, which the standing
         * prohibition names as an exception in as many words.** It also has to
         * say the one thing a person cannot see for themselves: that their work
         * was not necessarily lost, and which button does what. A bare "an
         * error occurred" would leave somebody guessing whether the action they
         * just took went through.
         */}
        <p className="m-0 text-fg-secondary">
          The last action may still have gone through — check the record before trying it again.
          Nothing else in SEED EMERGE is affected, so you can move to another screen without
          reloading.
        </p>
        {/*
         * The raw JavaScript message (`Cannot read properties of undefined …`)
         * means nothing to the person reading this screen, so it sits behind a
         * native disclosure rather than under the friendly copy. Kept, not
         * deleted: it is what somebody reporting the fault can paste. A native
         * `<details>` uses nothing that can throw, per the note above.
         */}
        <details className="text-xs text-fg-muted">
          <summary className="cursor-pointer select-none text-fg-secondary">Technical details</summary>
          <p className="m-0 mt-1.5 [overflow-wrap:anywhere]">{error.message}</p>
        </details>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={this.retry}
            className="h-9 rounded-md bg-accent-brand px-3 text-xs font-medium text-fg-on-accent max-sm:h-11"
          >
            Try this screen again
          </button>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="h-9 rounded-md border border-border px-3 text-xs font-medium text-fg-secondary max-sm:h-11"
          >
            Reload the page
          </button>
        </div>
      </div>
    );
  }
}
